/**
 * Apple emoji (Telegram's look) instead of whatever emoji font the device has.
 * The pictures are two atlases (built by scripts/build-emoji-atlas.mjs) and an emoji is a window onto one of them:
 * the "hot" one holds the few hundred emoji people really send (loaded at idle, ~400 KB); the "full" one holds the
 * rest at a smaller size and is downloaded by the browser only when a message actually shows one of them.
 * An emoji that is not in the atlas is left as the character itself.
 */
import emojiRegex from "emoji-regex";
import atlas from "../assets/emojiAtlas.json";

export const ATLAS_URL = "/emoji/apple-hot.webp";
const FULL_URL = "/emoji/apple-full.webp";

/** The atlas key of an emoji: its code points without U+FE0F (people type both forms). */
const keyOf = (emoji) =>
  [...emoji]
    .map((c) => c.codePointAt(0).toString(16))
    .filter((cp) => cp !== "fe0f")
    .join("-");

/** Style variables that point an emoji at its cell: the hot atlas is the CSS default, the full one is named explicitly. */
function cellVars(emoji) {
  const key = keyOf(emoji);
  const hot = atlas.hot.cells[key];
  if (hot) return { "--ex": hot[0], "--ey": hot[1] };
  const full = atlas.full.cells[key];
  if (full) return { "--ex": full[0], "--ey": full[1], "--ec": atlas.full.cols, "--er": atlas.full.rows, "--eimg": `url(${FULL_URL})` };
  return null;
}

/** Inline style that shows the emoji's cell of the atlas at 1em-based size (the .apple-emoji class sets the size), or null. */
export function appleEmojiStyle(emoji) {
  return cellVars(emoji);
}

const escapeAttr = (s) => s.replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;");

function emojiHtml(emoji) {
  const vars = cellVars(emoji);
  if (!vars) return emoji;
  const style = Object.entries(vars)
    .map(([k, v]) => `${k}:${v}`)
    .join(";");
  return `<span class="apple-emoji" role="img" aria-label="${escapeAttr(emoji)}" style="${style}"></span>`;
}

/** Emoji in the text parts of an HTML string (never inside tags) become Apple emoji. */
export function emojifyHtml(html) {
  if (!html || typeof html !== "string") return html;
  const re = emojiRegex();
  if (!re.test(html)) return html;
  return html
    .split(/(<[^>]*>)/)
    .map((part) => (part.startsWith("<") ? part : part.replace(emojiRegex(), emojiHtml)))
    .join("");
}

// the atlas is fetched once the browser is idle, so the first emoji in a chat is not a late pop-in
if (typeof window !== "undefined" && !window.__appleEmojiWarm) {
  window.__appleEmojiWarm = true;
  const warm = () => {
    const i = new Image();
    i.decoding = "async";
    i.src = ATLAS_URL;
  };
  (window.requestIdleCallback || ((f) => setTimeout(f, 1500)))(warm);
}
